import React, { useEffect, useState } from 'react';
import { CheckCircle, XCircle, Clock, User, RefreshCw, Package, Filter } from 'lucide-react';
import { getPembelianVerifikasiLog } from '../../api';

export default function LogVerifikasiPembelian() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all' | 'Diterima' | 'Ditolak'

  const load = () => {
    setLoading(true);
    getPembelianVerifikasiLog()
      .then(res => setData(res.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  // Auto-refresh when tab becomes visible
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const rupiah = (v) => `Rp ${(Number(v) || 0).toLocaleString('id-ID')}`;

  const formatDate = (d) => {
    if (!d) return '-';
    const dt = new Date(d);
    return dt.toLocaleDateString('id-ID', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const statusBadge = (status) => {
    if (status === 'Diterima') {
      return (
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '3px 10px', borderRadius: 20, fontSize: '0.75rem',
          fontWeight: 600, background: '#d1fae5', color: '#065f46'
        }}>
          <CheckCircle size={12} /> Disetujui
        </span>
      );
    }
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '3px 10px', borderRadius: 20, fontSize: '0.75rem',
        fontWeight: 600, background: '#fee2e2', color: '#991b1b'
      }}>
        <XCircle size={12} /> Ditolak
      </span>
    );
  };

  const filtered = filter === 'all' ? data : data.filter(d => d.status === filter);
  const totalApproved = data.filter(d => d.status === 'Diterima').length;
  const totalRejected = data.filter(d => d.status === 'Ditolak').length;

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Package size={24} />
              Log Verifikasi Pembelian
            </h2>
            <p className="page-desc">
              Riwayat siapa yang menyetujui atau menolak pembelian sparepart
            </p>
          </div>
          <button className="btn btn-secondary" onClick={load} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'spinner' : ''} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginBottom: 20 }}>
        <div style={{ background: '#f0fdf4', borderRadius: 12, padding: '16px 20px', border: '1px solid #bbf7d0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CheckCircle size={20} style={{ color: '#16a34a' }} />
            </div>
            <div>
              <div style={{ fontSize: '0.72rem', color: '#166534', fontWeight: 500 }}>Disetujui</div>
              <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#166534' }}>{totalApproved}</div>
            </div>
          </div>
        </div>
        <div style={{ background: '#fef2f2', borderRadius: 12, padding: '16px 20px', border: '1px solid #fecaca' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <XCircle size={20} style={{ color: '#dc2626' }} />
            </div>
            <div>
              <div style={{ fontSize: '0.72rem', color: '#991b1b', fontWeight: 500 }}>Ditolak</div>
              <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#991b1b' }}>{totalRejected}</div>
            </div>
          </div>
        </div>
        <div style={{ background: '#eff6ff', borderRadius: 12, padding: '16px 20px', border: '1px solid #bfdbfe' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Package size={20} style={{ color: '#2563eb' }} />
            </div>
            <div>
              <div style={{ fontSize: '0.72rem', color: '#1e40af', fontWeight: 500 }}>Total Pembelian</div>
              <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#1e40af' }}>{data.length}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <Filter size={16} style={{ color: '#6b7280' }} />
        <span style={{ fontSize: '0.85rem', color: '#6b7280', fontWeight: 500 }}>Filter:</span>
        {[
          { key: 'all', label: 'Semua' },
          { key: 'Diterima', label: `Disetujui (${totalApproved})` },
          { key: 'Ditolak', label: `Ditolak (${totalRejected})` },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            style={{
              padding: '5px 14px', borderRadius: 20, border: '1px solid',
              borderColor: filter === f.key ? '#6366f1' : '#d1d5db',
              background: filter === f.key ? '#eef2ff' : 'white',
              color: filter === f.key ? '#4338ca' : '#374151',
              fontSize: '0.78rem', fontWeight: 500, cursor: 'pointer',
              transition: 'all 0.15s'
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading && filtered.length === 0 ? (
        <div className="page-loading">Memuat data...</div>
      ) : filtered.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '60px 20px',
          background: '#f9fafb', borderRadius: 16,
          border: '1px dashed #d1d5db'
        }}>
          <Clock size={48} style={{ color: '#9ca3af', marginBottom: 12, opacity: 0.5 }} />
          <h3 style={{ color: '#374151', marginBottom: 6, fontSize: '1.1rem' }}>Belum Ada Verifikasi</h3>
          <p style={{ color: '#9ca3af', fontSize: '0.9rem' }}>
            Belum ada pembelian yang diverifikasi. Riwayat verifikasi akan muncul di sini.
          </p>
        </div>
      ) : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>No. Ref</th>
                <th>Tanggal</th>
                <th>Supplier</th>
                <th>Qty</th>
                <th>Total</th>
                <th>Status</th>
                <th>Diverifikasi Oleh</th>
                <th>Waktu Verifikasi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(d => (
                <tr key={d.id} style={{ background: d.status === 'Diterima' ? '#fafdf6' : '#fefafa' }}>
                  <td><strong style={{ fontSize: '0.8rem' }}>{d.no_ref}</strong></td>
                  <td>{d.tanggal}</td>
                  <td style={{ fontWeight: 500 }}>{d.supplier_name || '-'}</td>
                  <td>{d.total_qty}</td>
                  <td style={{ fontWeight: 700, color: '#059669' }}>{rupiah(d.total_amount)}</td>
                  <td>{statusBadge(d.status)}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{
                        width: 26, height: 26, borderRadius: '50%',
                        background: d.status === 'Diterima' ? '#dcfce7' : '#fee2e2',
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}>
                        <User size={13} style={{ color: d.status === 'Diterima' ? '#16a34a' : '#dc2626' }} />
                      </div>
                      <span style={{ fontWeight: 500, fontSize: '0.88rem' }}>
                        {d.verified_name || d.verified_by || '-'}
                      </span>
                    </div>
                  </td>
                  <td style={{ fontSize: '0.82rem', color: '#6b7280' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Clock size={12} />
                      {formatDate(d.verified_at)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
